import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ChevronRight, FileText, Loader2, Search } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { P, PermissionGuard, RequirePermission } from '@/features/permissions';
import { type TemplateOut, useTemplates } from '../api/templates-api';
import { TemplateFormDialog } from '../components/TemplateFormDialog';
import { usePageHeaderSetter } from '@/components/layout/PageHeaderContext';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export function TemplatesPage() {
  return (
    <PermissionGuard permission={P.READ_TEMPLATE}>
      <TemplatesPageContent />
    </PermissionGuard>
  );
}

function TemplatesPageContent() {
  const navigate = useNavigate();
  const setPageHeader = usePageHeaderSetter();
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<'document' | 'context_view'>('document');
  const { data, isLoading } = useTemplates({ limit: 100, category: activeCategory });

  useEffect(() => {
    setPageHeader({
      title: 'Templates',
      actions: (
        <RequirePermission permission={P.CREATE_TEMPLATE}>
          <TemplateFormDialog category={activeCategory} />
        </RequirePermission>
      ),
    });
    return () => setPageHeader(null);
  }, [setPageHeader, activeCategory]);

  const templates = useMemo(() => {
    const items = data?.data ?? [];
    if (!search.trim()) return items;
    const term = search.toLowerCase();
    return items.filter((t) => t.name.toLowerCase().includes(term));
  }, [data?.data, search]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      {/* Stats */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Templates</p>
                <p className="text-2xl font-bold">{data?.meta.total_items ?? 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category Toggle */}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant={activeCategory === 'document' ? 'default' : 'outline'}
          onClick={() => setActiveCategory('document')}
        >
          Document Templates
        </Button>
        <Button
          size="sm"
          variant={activeCategory === 'context_view' ? 'default' : 'outline'}
          onClick={() => setActiveCategory('context_view')}
        >
          Context Views
        </Button>
      </div>

      {/* Search */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            className="pl-9"
            placeholder="Search by name..."
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Name</TableHead>
              <TableHead className="hidden md:table-cell">Description</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden md:table-cell">Created</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.map((template) => (
              <TemplateRow
                key={template.id}
                template={template}
                onClick={() =>
                  navigate({
                    to: '/templates/$templateId',
                    params: { templateId: template.id },
                  })
                }
              />
            ))}
            {templates.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  No templates found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Showing {templates.length} of {data?.meta.total_items ?? 0} templates
      </p>
    </div>
  );
}

function TemplateRow({
  onClick,
  template,
}: {
  template: TemplateOut;
  onClick: () => void;
}) {
  return (
    <TableRow className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={onClick}>
      <TableCell>
        <p className="font-medium">{template.name}</p>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <span className="text-sm text-muted-foreground">
          {template.description
            ? template.description.length > 60
              ? `${template.description.slice(0, 60)}...`
              : template.description
            : '-'}
        </span>
      </TableCell>
      <TableCell>
        <Badge variant={template.is_active ? 'default' : 'secondary'}>
          {template.is_active ? 'Active' : 'Inactive'}
        </Badge>
      </TableCell>
      <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
        {format(new Date(template.created_at), 'MMM d, yyyy')}
      </TableCell>
      <TableCell>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </TableCell>
    </TableRow>
  );
}
